import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from './AuthContext';
import { Lock, Mail, Eye, EyeOff } from 'lucide-react';

const LOGO = 'https://customer-assets.emergentagent.com/job_bbc14d05-1309-4872-bdcf-991a413c9c8e/artifacts/9i1h196a_JCS_monograma_transparente.png';

export default function Login() {
  const { login, user } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPw, setShowPw] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  // If already logged in, redirect
  React.useEffect(() => {
    if (user) navigate('/escritorio/dashboard', { replace: true });
  }, [user, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      await login(email.trim(), password);
      navigate('/escritorio/dashboard', { replace: true });
    } catch (err) {
      const detail = err?.response?.data?.detail;
      setError(typeof detail === 'string' ? detail : 'Credenciais inválidas');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#FDFBF7] flex items-center justify-center px-4">
      <div className="w-full max-w-sm">
        {/* Logo */}
        <div className="flex flex-col items-center mb-10">
          <img src={LOGO} alt="JCS" className="h-16 w-auto mb-5" />
          <h1 className="font-serif text-2xl text-[#1A1A1A] tracking-tight">Portal Interno</h1>
          <p className="font-sans text-xs text-[#AAAAAA] tracking-widest uppercase mt-1">Acesso Restrito</p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4" data-testid="login-form">
          {/* Email */}
          <div>
            <label className="font-sans text-xs text-[#4A4A4A] uppercase tracking-widest block mb-2">Email</label>
            <div className="relative">
              <Mail size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#AAAAAA]" />
              <input
                type="email"
                value={email}
                onChange={e => setEmail(e.target.value)}
                required
                data-testid="login-email-input"
                className="w-full pl-9 pr-4 py-3 border border-[#E5E0D8] bg-white font-sans text-sm text-[#1A1A1A] focus:outline-none focus:border-[#6B2D3E] transition-colors"
                placeholder="email@exemplo.com"
              />
            </div>
          </div>

          {/* Password */}
          <div>
            <label className="font-sans text-xs text-[#4A4A4A] uppercase tracking-widest block mb-2">Password</label>
            <div className="relative">
              <Lock size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#AAAAAA]" />
              <input
                type={showPw ? 'text' : 'password'}
                value={password}
                onChange={e => setPassword(e.target.value)}
                required
                data-testid="login-password-input"
                className="w-full pl-9 pr-10 py-3 border border-[#E5E0D8] bg-white font-sans text-sm text-[#1A1A1A] focus:outline-none focus:border-[#6B2D3E] transition-colors"
                placeholder="••••••••"
              />
              <button type="button" onClick={() => setShowPw(!showPw)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-[#AAAAAA] hover:text-[#4A4A4A]">
                {showPw ? <EyeOff size={15} /> : <Eye size={15} />}
              </button>
            </div>
          </div>

          {/* Error */}
          {error && (
            <p className="font-sans text-sm text-red-600" data-testid="login-error">{error}</p>
          )}

          {/* Submit */}
          <button
            type="submit"
            disabled={loading}
            data-testid="login-submit-btn"
            className="w-full py-3 bg-[#6B2D3E] text-white font-sans text-xs tracking-widest uppercase hover:bg-[#5A2535] transition-colors disabled:opacity-50 mt-2"
          >
            {loading ? 'A entrar...' : 'Entrar'}
          </button>
        </form>

        <p className="font-sans text-xs text-[#CCCCCC] text-center mt-8">
          © {new Date().getFullYear()} JCS — Portal Interno
        </p>
      </div>
    </div>
  );
}
